import {
  definitionsHash,
  inputForLevel,
} from '../../generation/curve';
import { generateLevelOrThrow, levelContentHash } from '../../generation/generator';
import {
  assertManifestHeader,
  decodeLevelPack,
  decodeManifestEntry,
  decodeManifestIndex,
  type ManifestLevelEntry,
} from '../../generation/manifest';
import type { LevelDefinition } from '../model/types';
import manifestJson from './manifest.json';

const manifest = decodeManifestIndex(manifestJson);
assertManifestHeader(manifest, definitionsHash());

export const minimumLevelNumber = 1;
export const maximumLevelNumber = manifest.levelCount;

const packModules = import.meta.glob<{ default: unknown }>('./packs/*.json');
const entries = new Map<number, ManifestLevelEntry>();
const loadingPacks = new Map<number, Promise<void>>();

export function clampLevelNumber(levelNumber: number): number {
  return Math.min(
    Math.max(Math.round(levelNumber), minimumLevelNumber),
    maximumLevelNumber,
  );
}

function assertManifestLevel(levelNumber: number): void {
  if (!Number.isInteger(levelNumber) || levelNumber < 1 || levelNumber > manifest.levelCount) {
    throw new Error(`Level ${levelNumber} is not present in the manifest.`);
  }
}

function packNumberForLevel(levelNumber: number): number {
  return Math.floor((levelNumber - 1) / manifest.packSize) + 1;
}

function packPath(packNumber: number): string {
  return `./packs/pack-${String(packNumber).padStart(3, '0')}.json`;
}

async function loadPack(packNumber: number): Promise<void> {
  const path = packPath(packNumber);
  const load = packModules[path];
  if (load === undefined) {
    throw new Error(`Level pack ${path} is missing.`);
  }
  const module = await load();
  const pack = decodeLevelPack(module.default);
  const firstLevel = (packNumber - 1) * manifest.packSize + 1;
  const expectedCount = Math.min(manifest.packSize, manifest.levelCount - firstLevel + 1);
  if (pack.levels.length !== expectedCount) {
    throw new Error(
      `Level pack ${path} contains ${pack.levels.length} levels; expected ${expectedCount}.`,
    );
  }
  const decoded = pack.levels.map((tuple, index) => decodeManifestEntry(tuple, index));
  decoded.forEach((entry, index) => {
    const expectedLevel = firstLevel + index;
    if (entry.levelNumber !== expectedLevel) {
      throw new Error(
        `Level pack ${path} entry ${index} is level ${entry.levelNumber}; expected ${expectedLevel}.`,
      );
    }
  });
  decoded.forEach((entry) => entries.set(entry.levelNumber, Object.freeze(entry)));
}

export function manifestSummary(): { levelCount: number; packSize: number } {
  return { levelCount: manifest.levelCount, packSize: manifest.packSize };
}

export async function ensureLevelLoaded(levelNumber: number): Promise<void> {
  assertManifestLevel(levelNumber);
  if (entries.has(levelNumber)) {
    return;
  }
  const packNumber = packNumberForLevel(levelNumber);
  let loading = loadingPacks.get(packNumber);
  if (loading === undefined) {
    loading = loadPack(packNumber);
    loadingPacks.set(packNumber, loading);
  }
  try {
    await loading;
  } finally {
    loadingPacks.delete(packNumber);
  }
}

export function isLevelLoaded(levelNumber: number): boolean {
  return entries.has(levelNumber);
}

export function getEntry(levelNumber: number): ManifestLevelEntry {
  assertManifestLevel(levelNumber);
  const entry = entries.get(levelNumber);
  if (entry === undefined) {
    throw new Error(
      `Level ${levelNumber} is not loaded. Call ensureLevelLoaded(${levelNumber}) first.`,
    );
  }
  return entry;
}

export function buildLevelByNumber(levelNumber: number): LevelDefinition {
  const entry = getEntry(levelNumber);
  const result = generateLevelOrThrow(inputForLevel(
    levelNumber,
    entry.resolvedSeed,
    entry.maskId,
    entry.paramsId,
  ));
  const actualHash = levelContentHash(result.level);
  if (actualHash !== entry.contentHash) {
    throw new Error(
      `Level ${levelNumber} content hash mismatch: manifest=${entry.contentHash}, actual=${actualHash}.`,
    );
  }
  return result.level;
}
